import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, parseMoney, type CuentaBancoCordobas } from '@abastece/shared';
import { readSettings } from '../db/reader.js';
import { cuentaEnCordobas } from '../lib/bancos.js';
import { FORMAS_PARTE, type FormaParte } from '../lib/pagos-de-compra.js';
import { useT, type Clave } from '../i18n/index.js';
import { Campo, Selector } from './Campo.js';
import { ElegirCuenta } from './ElegirCuenta.js';
import { Ico } from './Iconos.js';

/**
 * The parts of something paid more than one way: «Mercadería comprada»'s (the owner, 2026-10-04)
 * and, since 2026-10-16, a new expense's in «Gastos» (the owner: «We need the option to pay with
 * mixed methods of payment»). A row for each part: how, and how much in the currency of what is
 * paid, the córdoba account a part by transfer came from, and «Quitar»; «Agregar otro pago» under
 * them, six at most, as the events take them. What pays the rest, and the button that adds the
 * first part, stay with each form, which reads the parts in the order they add up.
 */

/** A part as its form holds it: the amount as typed. */
export interface ParteEscrita {
  pagoId: string;
  formaPago: FormaParte;
  monto: string;
  /** By transfer: the córdoba account picked, or null for where transfers go. */
  cuenta: CuentaBancoCordobas | null;
}

const NOMBRE_PARTE: Record<FormaParte, Clave> = {
  efectivo_cordobas: 'pago.efectivo_cordobas',
  efectivo_dolares: 'gastos.efectivoUsd',
  transferencia: 'pago.transferencia',
  billetera: 'gastos.billeteraMovil',
};

/** Six at most, as `compra.registrada` and `gasto.registrado` take them. */
export const MAS_PARTES = 6;

export function PartesDelPago({
  partes,
  moneda,
  cambiar,
  quitar,
  agregar,
  enfocar,
  datos,
  className = 'flex flex-col gap-3',
}: {
  partes: readonly ParteEscrita[];
  moneda: 'NIO' | 'USD';
  cambiar: (pagoId: string, patch: Partial<ParteEscrita>) => void;
  quitar: (pagoId: string) => void;
  agregar: () => void;
  /** The part just added, whose amount takes the cursor. */
  enfocar: string | null;
  /** The screen's name in its hooks for the browser checks: `data-compra-partes`, `data-gasto-parte`… */
  datos: 'compra' | 'gasto';
  className?: string;
}) {
  const t = useT();
  const ajustes = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  if (partes.length === 0) return null;
  return (
    <div className={className} {...{ [`data-${datos}-partes`]: true }}>
      <p className="etiqueta mb-0">{t('compra.partes')}</p>
      {partes.map((x) => {
        const monto = parseMoney(x.monto);
        return (
          <div
            key={x.pagoId}
            className="flex flex-col gap-2 rounded-xl bg-slate-950/40 p-3 ring-1 ring-white/5 ring-inset"
            {...{ [`data-${datos}-parte`]: true }}
          >
            {/* One above the other on a phone: side by side, «Transferencia» did not fit its select. */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Selector
                etiqueta={t('compra.parteForma')}
                value={x.formaPago}
                onChange={(e) => cambiar(x.pagoId, { formaPago: e.target.value as FormaParte })}
              >
                {FORMAS_PARTE.map((f) => (
                  <option key={f} value={f}>
                    {t(NOMBRE_PARTE[f])}
                  </option>
                ))}
              </Selector>
              <Campo
                etiqueta={t('compra.parteMonto', { moneda: moneda === 'USD' ? 'US$' : 'C$' })}
                inputMode="decimal"
                className="tabular"
                value={x.monto}
                onChange={(e) => cambiar(x.pagoId, { monto: e.target.value })}
                placeholder="0.00"
                autoFocus={x.pagoId === enfocar}
                error={x.monto.trim() !== '' && !((monto ?? 0) > 0) ? t('caja.montoInvalido') : null}
              />
            </div>
            {x.formaPago === 'transferencia' && (
              <ElegirCuenta
                valor={cuentaEnCordobas(ajustes, x.cuenta)}
                onCambiar={(c) => cambiar(x.pagoId, { cuenta: c as CuentaBancoCordobas })}
                etiqueta={t('bancos.pagaDe')}
              />
            )}
            <button type="button" className="btn-texto gap-1 self-end text-xs text-slate-500" onClick={() => quitar(x.pagoId)}>
              <Ico icono="quitar" className="size-3.5" />
              {t('comun.quitar')}
            </button>
          </div>
        );
      })}
      {partes.length < MAS_PARTES && (
        <button
          type="button"
          className="btn-texto gap-1.5 self-start text-sm"
          onClick={agregar}
          {...{ [`data-${datos}-otro-pago`]: true }}
        >
          <Ico icono="nuevo" className="size-4" />
          {t('compra.otroPago')}
        </button>
      )}
    </div>
  );
}
